import { useCallback, useEffect, useState } from "react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { TrendingUp, TrendingDown, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";

/**
 * Iter 68 — CashForecastCard.
 *
 * A compact 30-day cash-flow forecast widget for the Dashboard. Uses the
 * existing `/reports/cash-forecast?weeks=4` endpoint and renders a
 * sparkline of the projected running cash balance with a headline metric:
 * "You'll end the month at $X" plus an at-a-glance warning if the balance
 * goes negative at any point (runway red flag).
 */
export default function CashForecastCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reports/cash-forecast?weeks=4");
      setData(data);
      setErr(false);
    } catch {
      setErr(true);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (err) return null;
  if (!data) {
    return (
      <div className="card-flat p-5" data-testid="dashboard-cash-forecast-loading">
        <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">30-day cash forecast</div>
        <div className="text-sm text-[var(--muted-text)] mt-2">Loading…</div>
      </div>
    );
  }

  const weeks = data.buckets || [];
  const currency = data.currency || "USD";
  const start = data.starting_cash ?? 0;
  const end = weeks.length ? weeks[weeks.length - 1].ending_cash : start;
  const delta = end - start;
  const anyNegative = weeks.some((w) => w.ending_cash < 0);
  const chartData = [
    { week: "Now", cash: start },
    ...weeks.map((w, i) => ({ week: `W${i + 1}`, cash: w.ending_cash })),
  ];

  return (
    <div className="card-flat p-5" data-testid="dashboard-cash-forecast">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">30-day cash forecast</div>
          <div className="font-display text-2xl mt-1">
            End of month: <span className={end < 0 ? "text-[#B4283A]" : "text-[#2F4F4F]"}>{formatCurrency(end, currency)}</span>
          </div>
          <div className="text-xs text-[var(--muted-text)] mt-0.5 flex items-center gap-2">
            {delta >= 0 ? (
              <><TrendingUp className="w-3.5 h-3.5 text-[#4C956C]" /> <span className="text-[#4C956C]">+{formatCurrency(delta, currency)}</span> vs today</>
            ) : (
              <><TrendingDown className="w-3.5 h-3.5 text-[#B4283A]" /> <span className="text-[#B4283A]">−{formatCurrency(Math.abs(delta), currency)}</span> vs today</>
            )}
          </div>
        </div>
        {anyNegative && (
          <div data-testid="cash-forecast-warning" className="flex items-start gap-1.5 text-[10px] text-[#B4283A] max-w-[180px] text-right">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>Cash dips negative at some point this month. Review upcoming bills.</span>
          </div>
        )}
      </div>
      <div style={{ width: "100%", height: 96 }}>
        <ResponsiveContainer>
          <LineChart data={chartData} margin={{ top: 6, right: 8, left: 8, bottom: 0 }}>
            <XAxis dataKey="week" fontSize={10} tick={{ fill: "#7A6C4E" }} axisLine={false} tickLine={false} />
            <YAxis hide domain={["auto", "auto"]} />
            <Tooltip
              formatter={(v) => formatCurrency(v, currency)}
              labelStyle={{ fontSize: 11, color: "#7A6C4E" }}
              contentStyle={{ fontSize: 12, border: "1px solid #E5E1D5", borderRadius: 6 }}
            />
            <Line type="monotone" dataKey="cash" stroke={anyNegative ? "#B4283A" : "#2F4F4F"} strokeWidth={2} dot={{ r: 3, fill: "#2F4F4F" }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 flex items-center justify-between text-[10px] text-[var(--muted-text)]">
        <div>Starting: <span className="font-mono">{formatCurrency(start, currency)}</span></div>
        <a href="/financials?tab=forecast" className="text-[var(--brand)] hover:underline">See 13-week outlook →</a>
      </div>
    </div>
  );
}
